import BigNumber from "bignumber.js";
import {
  truncateDecimals,
  formatWithSignificantDigits,
} from "./numberFormatter";

class CurrencyFormatter {
  constructor(options = {}) {
    this.locale = options.locale || "en-ZA";
    this.decimalPlaces = options.decimalPlaces || 2;
    this.cryptocurrencyDecimalPlaces = options.cryptocurrencyDecimalPlaces || 2;
    this.stablecoins = options.stablecoins || ["USDT", "USDC", "DAI"];
    this.fiat = ["ZAR", "USD"];
    this.twoDecimalCurrencies = ["USD", "ZAR", "USDT", "USDC"];
    this.useDynamicDecimals = options.useDynamicDecimals || false;
  }

  formatFiat(amount, currency) {
    let cleanAmount = amount || 0;
    if (typeof cleanAmount === "string") {
      const commaDecimalMatch = cleanAmount.match(/^([\d\s]+),(\d{1,2})$/);
      if (commaDecimalMatch) {
        cleanAmount = `${commaDecimalMatch[1].replace(/\s/g, "")}.${commaDecimalMatch[2]}`;
      } else {
        cleanAmount = cleanAmount.replace(/,/g, "");
      }
    }

    const numAmount =
      typeof cleanAmount === "string"
        ? parseFloat(cleanAmount) || 0
        : cleanAmount;
    const bigAmount = new BigNumber(numAmount);

    const locale = "en-US";

    const currencyFormatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency === "ZAR" ? "ZAR" : currency || "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    const parts = currencyFormatter.formatToParts(0);
    const currencySymbol =
      parts.find((part) => part.type === "currency")?.value ||
      (currency === "ZAR" ? "R" : "$");

    const currencyUpper = currency?.toUpperCase();
    const mustUseTwoDecimals =
      this.twoDecimalCurrencies.includes(currencyUpper);
    const fiatDecimalPlaces = 2;

    if (this.useDynamicDecimals && !mustUseTwoDecimals) {
      let decimalPlaces;
      if (bigAmount.isZero()) {
        decimalPlaces = fiatDecimalPlaces;
      } else {
        const integerPart = bigAmount.abs().integerValue();
        const integerDigits = integerPart.toString().length;
        decimalPlaces = Math.max(fiatDecimalPlaces, 8 - integerDigits);
      }

      const formattedAmount = truncateDecimals(
        bigAmount.toNumber(),
        decimalPlaces
      );

      const isNegative = bigAmount.isNegative();
      const amountParts = formattedAmount.split(".");
      const integerPart = amountParts[0].replace(/^-/, "");
      let decimalPart = amountParts[1] || "";
      if (decimalPart.length < fiatDecimalPlaces) {
        decimalPart = decimalPart.padEnd(fiatDecimalPlaces, "0");
      } else if (decimalPart.length > fiatDecimalPlaces) {
        decimalPart = decimalPart.substring(0, fiatDecimalPlaces);
      }
      decimalPart = `.${decimalPart}`;

      const integerFormatted = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(parseInt(integerPart));

      const formatted = `${isNegative ? "-" : ""}${integerFormatted.replace(/,/g, " ")}${decimalPart}`;

      return `${currencySymbol} ${formatted}`;
    }

    const formattedAmount = truncateDecimals(
      bigAmount.toNumber(),
      fiatDecimalPlaces
    );
    const amountParts = formattedAmount.split(".");
    const integerPart = amountParts[0].replace(/^-/, "");
    let decimalPart = amountParts[1] || "";
    if (decimalPart.length < fiatDecimalPlaces) {
      decimalPart = decimalPart.padEnd(fiatDecimalPlaces, "0");
    } else if (decimalPart.length > fiatDecimalPlaces) {
      decimalPart = decimalPart.substring(0, fiatDecimalPlaces);
    }
    decimalPart = `.${decimalPart}`;
    const isNegative = bigAmount.isNegative();

    const integerFormatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(parseInt(integerPart));

    const formatted = `${isNegative ? "-" : ""}${integerFormatted.replace(/,/g, " ")}${decimalPart}`;
    return `${currencySymbol} ${formatted}`;
  }

  formatCrypto(amount, symbol) {
    const bigAmount = new BigNumber(amount);
    const symbolUpper = symbol?.toUpperCase();
    const numValue = bigAmount.toNumber();

    // Stablecoins and currencies that require 2 decimal places
    const mustUseTwoDecimals = this.twoDecimalCurrencies.includes(symbolUpper);
    if (this.stablecoins.includes(symbolUpper) || mustUseTwoDecimals) {
      return `${truncateDecimals(numValue, 2)} ${symbol}`;
    }

    // For all other crypto: use smart formatting with significant digits
    const maxDecimals =
      symbolUpper === "BTC" || symbolUpper === "BITCOIN" ? 8 : 6;
    const formatted = formatWithSignificantDigits(numValue, {
      minSignificant: 4,
      maxDecimals: maxDecimals,
    });

    return `${formatted} ${symbol}`;
  }

  format(amount, currencyOrSymbol = "USD", options = {}) {
    const originalUseDynamicDecimals = this.useDynamicDecimals;
    if (options.useDynamicDecimals !== undefined) {
      this.useDynamicDecimals = options.useDynamicDecimals;
    }

    const currencyUpper = currencyOrSymbol?.toUpperCase();
    const type =
      this.fiat.includes(currencyOrSymbol) || currencyUpper === "USD"
        ? "fiat"
        : "crypto";
    const result =
      type === "crypto"
        ? this.formatCrypto(amount, currencyOrSymbol)
        : this.formatFiat(amount, currencyOrSymbol);

    this.useDynamicDecimals = originalUseDynamicDecimals;

    return result;
  }
}

export default CurrencyFormatter;
